import React, { useRef, useEffect} from 'react';
import './PartnerPortal.css';
import { Lightbulb, DollarSign, Users, ArrowRight } from 'lucide-react';
import Partner from './partnerp.png';
import nvidiaLogo from './nvidia.png';
import intelLogo from './intel.png';
import microsoftLogo from './Microsoft-for-Startups.jpg';

const PartnerPortal = () => {
  const scrollToSection = () => {
    const element = document.getElementById('target-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToContact = () => {
    window.location.href = '/contact';
  };

  const partnershipTextRef = useRef(null);
  const benefitRefs = useRef([]);
  const ecosystemRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('in-view');
        });
      },
      { threshold: 0.3 }
    );

    const elements = [
      partnershipTextRef.current,
      ecosystemRef.current,
      ...benefitRefs.current
    ].filter(Boolean);

    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const partnerLogos = [
    { name: 'NVIDIA', logo: nvidiaLogo },
    { name: 'Intel', logo: intelLogo },
    { name: 'Microsoft', logo: microsoftLogo }
  ];

  // Debug: Log the imported images
  console.log('Partner logos:', { nvidiaLogo, intelLogo, microsoftLogo });

  const benefits = [
    {
      icon: Lightbulb,
      title: 'Be Innovative',
      description: 'Deliver modern digital solutions using Erasys\' powerful technologies, APIs, and automation frameworks.'
    },
    {
      icon: DollarSign,
      title: 'Boost Profitability',
      description: 'Expand your revenue streams with flexible partnership models and access to Erasys\' growing customer base.'
    },
    {
      icon: Users,
      title: 'Strengthen Collaboration',
      description: 'Build long-term partnerships with dedicated Erasys support, joint development, and shared success opportunities.'
    }
  ];

  return (
    <div className="partner-portal-container">
      {/* Hero Section */}
      <section className="partner-portal-hero">
        <div className="partner-portal-hero-content">
          <h1 className="partner-portal-hero-title">
            <span className="partner-portal-hero-main">Erasys</span>
            <span className="partner-portal-hero-accent">Partner Portal</span>
          </h1>
          <p className="partner-portal-hero-subtitle">
            Partner with Erasys to boost your business and offer services that exceed customer expectations and drive growth utilizing our innovative software solutions.
          </p>
          <div className="partner-portal-hero-buttons">
            <button className="partner-portal-btn partner-portal-btn-primary" onClick={scrollToSection}>
              Learn More
              <ArrowRight className="partner-portal-btn-icon" />
            </button>
          </div>
          
          {/* Partner Logos Carousel */}
          <div className="partner-logos-carousel">
            <div className="partner-logos-track">
              {[...partnerLogos, ...partnerLogos].map((partner, index) => (
                <div key={index} className="partner-logo-item">
                  <img 
                    src={partner.logo} 
                    alt={`${partner.name} logo`}
                    className="partner-logo-image"
                    onLoad={() => console.log(`${partner.name} logo loaded successfully`)}
                    onError={(e) => console.error(`Failed to load ${partner.name} logo:`, e)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="partner-portal-partnership" id="target-section">
        <div className="partner-portal-section-wrapper">
          <div className="partner-portal-partnership-grid">
            <div className="partner-portal-partnership-image">
              <img src={Partner} alt="Partner Illustration" />
            </div>
            <div className="partner-portal-partnership-content" ref={partnershipTextRef}>
              <h2 className="partner-portal-partnership-title">Become an Erasys Partner</h2>
              <p className="partner-portal-partnership-description">
                Whether you're a <strong>Reseller Partner</strong> looking to expand your product offering or a <strong>Technical Collaboration Partner</strong> aiming to integrate and innovate with Erasys, we're here to help you succeed.
              </p>
              <p className="partner-portal-partnership-description">
                Join our growing network and gain access to exclusive resources, co-marketing opportunities, and cutting-edge solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="partner-portal-benefits">
        <div className="partner-portal-section-wrapper">
          <div className="partner-portal-section-header">
            <h2 className="partner-portal-section-title">Why partner with Erasys?</h2>
            <p className="partner-portal-section-subtitle">
              Erasys empowers partners with innovative tools, trusted collaboration models, and revenue-driving solutions that elevate your business and customer value.
            </p>
          </div>
          
          <div className="partner-portal-benefits-grid">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div
                  key={index}
                  className="partner-portal-benefit-card"
                  ref={(el) => (benefitRefs.current[index] = el)}
                >
                  <div className="partner-portal-benefit-icon">
                    <IconComponent strokeWidth={1.5} />
                  </div>
                  <h3 className="partner-portal-benefit-title">{benefit.title}</h3>
                  <p className="partner-portal-benefit-description">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ecosystem Section */}
      <section className="partner-portal-ecosystem">
        <div className="partner-portal-section-wrapper">
          <div className="partner-portal-ecosystem-content" ref={ecosystemRef}>
            <h2 className="partner-portal-ecosystem-title">Join our Partner Ecosystem</h2>
            <p className="partner-portal-ecosystem-description">
              Partner with Erasys to solve your customers' most pressing digital challenges. Our ecosystem combines intelligent solutions, technical innovation, and a global-first approach. Together, we position you as a strategic advisor backed by cutting-edge tools and enterprise-grade infrastructure.
            </p>
            <button className="partner-portal-btn partner-portal-btn-primary" onClick={goToContact}>
              Contact Us
              <ArrowRight className="partner-portal-btn-icon" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnerPortal;